/**
 * CDM Investigation page — for any golden_id, show every field, every
 * source's value, every conflict, every write-back, the full timeline.
 *
 * This is the killer feature vs generic CAASM: the lineage panel
 * answers "why is this OS value what it is?" without the user needing
 * to ask. See docs/cdm-design-v4.md §13.2.
 */
import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  Search, ChevronLeft, AlertTriangle, ArrowUpRight, Tag,
  Clock, Server, Database, Eye, Shield, Building2,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface RiskFactor {
  name: string
  weight: number
  triggered: boolean
  contribution: number
  value: any
  explanation: string
}

interface RiskScoreData {
  asset_id: string
  total: number
  band: string
  computed_at: string
  factors: RiskFactor[]
}

interface FieldObservation {
  source: string
  value: any
  confidence: number
  role: string
  observed_at: string | null
  recorded_at: string | null
}

interface Conflict {
  conflict_id: string
  field: string
  event: string
  actor: string | null
  sources: any
  notes: string | null
  occurred_at: string | null
}

interface Writeback {
  id: string
  field: string
  status: string
  source: string
  canonical_value: any
  intended_at: string | null
  pushed_at: string | null
  verified_at: string | null
  approver_id: string | null
}

interface TimelineEvent {
  kind: string
  field?: string
  source?: string
  value?: any
  status?: string
  event?: string
  actor?: string | null
  occurred_at: string | null
}

interface CdmTag {
  key: string
  value: any
  set_by: string
  set_at: string
}

interface TenantInfo {
  org_id: string
  name: string
  kind: string
  jurisdiction: string | null
  regulatory_zone: string | null
  data_residency: string | null
  isolation_mode: string | null
}

interface Investigation {
  golden_id: string
  tenant?: TenantInfo
  cross_tenant_view?: boolean
  scoped_org_id?: string | null
  fields: Record<string, FieldObservation[]>
  conflicts: Conflict[]
  writebacks: Writeback[]
  tags: CdmTag[]
  timeline: TimelineEvent[]
}

const ROLE_TONE: Record<string, string> = {
  observation:     'bg-blue/10 text-blue-light border-blue/30',
  human_assertion: 'bg-purple/10 text-purple-light border-purple/30',
  verification:    'bg-emerald/10 text-emerald border-emerald/30',
}

const STATUS_TONE: Record<string, string> = {
  queued:           'bg-blue/10 text-blue-light border-blue/30',
  preflight_ok:     'bg-cyan/10 text-cyan border-cyan/30',
  awaiting_approval: 'bg-amber/10 text-amber-light border-amber/30',
  approved:         'bg-amber/10 text-amber-light border-amber/30',
  executing:        'bg-purple/10 text-purple-light border-purple/30',
  executed:         'bg-purple/15 text-purple-light border-purple/30',
  verified:         'bg-emerald/15 text-emerald border-emerald/30',
  failed_silent:    'bg-rose/15 text-rose-light border-rose/30',
  failed_error:     'bg-rose/15 text-rose-light border-rose/30',
  rolled_back:      'bg-text-muted/15 text-text-muted border-border',
}

export default function CdmInvestigationPage() {
  const { id } = useParams<{ id: string }>()
  const [data, setData] = useState<Investigation | null>(null)
  const [loading, setLoading] = useState(false)
  const [threat, setThreat] = useState<any | null>(null)
  const [risk, setRisk] = useState<RiskScoreData | null>(null)

  useEffect(() => {
    if (!id) return
    setLoading(true)
    Promise.all([
      api.get(`/api/cdm/investigation/${id}`),
      api.get(`/api/cdm/threat/${id}`),
      api.get(`/api/cdm/risk/${id}`),
    ])
      .then(([d, t, r]) => {
        setData(d.data)
        setThreat(t.data)
        setRisk(r.data)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [id])

  if (!id) return <div className="card p-6 text-text-muted text-sm">No golden ID provided.</div>

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/cdm" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> CDM
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Search size={18} className="text-blue-light" /> INVESTIGATION
        </h1>
        <code className="text-[11px] font-mono text-text-secondary">{id}</code>
      </div>

      {loading && <div className="card p-6 text-text-muted text-sm">Loading…</div>}

      {/* Track 4.18 — tenant banner. Shows the asset's owning tenant
          plus a yellow warning when the user is viewing it from a
          different scope (cross-tenant drill-down via global_view). */}
      {data?.tenant && (
        <div className={clsx(
          'card px-3 py-2 flex flex-wrap items-center gap-3 text-[11px]',
          data.cross_tenant_view ? 'border-amber/40 bg-amber/5' : 'border-border',
        )}>
          <Building2 size={13} className={clsx(
            data.cross_tenant_view ? 'text-amber-light' : 'text-blue-light',
          )} />
          <span className="text-text-muted">Tenant</span>
          <span className="font-semibold text-text-primary">{data.tenant.name}</span>
          <span className="badge text-[9px] uppercase tracking-wider border px-1 py-0.5 bg-surface-raised text-text-muted border-border">
            {data.tenant.kind.replace('_', ' ')}
          </span>
          {data.tenant.jurisdiction && (
            <span className="badge text-[9px] uppercase tracking-wider border px-1 py-0.5 bg-blue/10 text-blue-light border-blue/30 font-mono">
              {data.tenant.jurisdiction}
            </span>
          )}
          {data.tenant.regulatory_zone && (
            <span className="text-text-muted text-[10px]">{data.tenant.regulatory_zone}</span>
          )}
          {data.tenant.data_residency && (
            <span className="text-text-muted text-[10px]">{data.tenant.data_residency}</span>
          )}
          {data.tenant.isolation_mode && data.tenant.isolation_mode !== 'shared' && (
            <span className="badge text-[9px] uppercase tracking-wider border px-1 py-0.5 bg-rose/10 text-rose-light border-rose/30">
              {data.tenant.isolation_mode}
            </span>
          )}
          {data.cross_tenant_view && (
            <span className="ml-auto inline-flex items-center gap-1 text-amber-light font-medium">
              <AlertTriangle size={12} />
              Viewing asset from {data.tenant.name} — cross-tenant view (audited)
            </span>
          )}
        </div>
      )}

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Field provenance — left two columns */}
          <div className="lg:col-span-2 space-y-3">
            <div className="card p-4">
              <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-3">
                <Database size={14} /> Field provenance
              </h2>
              {Object.keys(data.fields).length === 0 && (
                <p className="text-xs text-text-muted">No observations yet for this entity.</p>
              )}
              <div className="space-y-3">
                {Object.entries(data.fields).map(([field, observations]) => (
                  <FieldRow key={field} field={field} observations={observations} />
                ))}
              </div>
            </div>

            {/* Conflicts */}
            {data.conflicts.length > 0 && (
              <div className="card p-4">
                <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-3">
                  <AlertTriangle size={14} className="text-amber-light" /> Conflicts ({data.conflicts.length})
                </h2>
                <table className="w-full text-xs">
                  <thead className="bg-surface-raised">
                    <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
                      <th className="text-left px-2 py-1.5">Field</th>
                      <th className="text-left px-2 py-1.5">State</th>
                      <th className="text-left px-2 py-1.5">Sources</th>
                      <th className="text-left px-2 py-1.5">When</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.conflicts.map(c => (
                      <tr key={c.conflict_id}>
                        <td className="px-2 py-1.5 font-mono text-text-primary">{c.field}</td>
                        <td className="px-2 py-1.5">
                          <span className="badge text-[9px] bg-amber/10 text-amber-light border-amber/30 border">{c.event}</span>
                        </td>
                        <td className="px-2 py-1.5 text-text-secondary">
                          {Array.isArray(c.sources) && c.sources.map((s: any, i: number) => (
                            <span key={i} className="block text-[10px] font-mono">
                              {s.source}: {JSON.stringify(s.value)}
                            </span>
                          ))}
                        </td>
                        <td className="px-2 py-1.5 font-mono text-[10px] text-text-muted">
                          {c.occurred_at && new Date(c.occurred_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Write-backs */}
            {data.writebacks.length > 0 && (
              <div className="card p-4">
                <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-3">
                  <ArrowUpRight size={14} className="text-purple-light" /> Write-backs ({data.writebacks.length})
                </h2>
                <table className="w-full text-xs">
                  <thead className="bg-surface-raised">
                    <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
                      <th className="text-left px-2 py-1.5">Field → Source</th>
                      <th className="text-left px-2 py-1.5">Status</th>
                      <th className="text-left px-2 py-1.5">Value</th>
                      <th className="text-left px-2 py-1.5">When</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.writebacks.map(w => (
                      <tr key={w.id}>
                        <td className="px-2 py-1.5 font-mono text-text-primary">{w.field} → {w.source}</td>
                        <td className="px-2 py-1.5">
                          <span className={clsx('badge text-[9px] border',
                                               STATUS_TONE[w.status] || 'bg-surface-raised text-text-muted border-border')}>
                            {w.status}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-text-secondary font-mono text-[10px]">
                          {JSON.stringify(w.canonical_value)}
                        </td>
                        <td className="px-2 py-1.5 font-mono text-[10px] text-text-muted">
                          {(w.verified_at || w.pushed_at || w.intended_at) &&
                           new Date(w.verified_at || w.pushed_at || w.intended_at!).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Tags */}
            {data.tags.length > 0 && (
              <div className="card p-4">
                <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-3">
                  <Tag size={14} className="text-cyan" /> Tags ({data.tags.length})
                </h2>
                <div className="flex flex-wrap gap-2">
                  {data.tags.map((t, i) => (
                    <span key={i} className="badge text-[10px] bg-surface-raised text-text-secondary border border-border">
                      <span className="text-cyan">{t.key}</span>: {JSON.stringify(t.value)}
                      <span className="text-text-muted ml-1">({t.set_by})</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right column: risk + threat correlation + timeline */}
          <div className="space-y-3">
            {risk && (
              <div className="card p-4">
                <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-3">
                  <Shield size={14} className={clsx(
                    risk.band === 'critical' ? 'text-rose-light' :
                    risk.band === 'high'     ? 'text-amber-light' :
                    risk.band === 'medium'   ? 'text-cyan' :
                                                'text-emerald'
                  )} /> Risk score
                  <span className="ml-auto text-xs uppercase tracking-wider font-mono">
                    {risk.band}
                  </span>
                </h2>
                <div className="flex items-center gap-2 mb-3">
                  <div className={clsx(
                    'text-3xl font-black font-mono',
                    risk.band === 'critical' ? 'text-rose-light' :
                    risk.band === 'high'     ? 'text-amber-light' :
                    risk.band === 'medium'   ? 'text-cyan' :
                                                'text-emerald'
                  )}>{risk.total}</div>
                  <div className="text-[10px] text-text-muted">/100</div>
                </div>
                <div className="space-y-1.5">
                  {risk.factors.map(f => (
                    <div key={f.name} className={clsx(
                      'flex items-center justify-between gap-2 text-[11px] py-1 px-2 rounded',
                      f.triggered ? 'bg-rose/5 border border-rose/20' : 'opacity-60',
                    )}>
                      <div className="flex-1 min-w-0">
                        <div className={clsx('font-mono text-[10px]',
                                             f.triggered ? 'text-rose-light' : 'text-text-muted')}>
                          {f.name}
                        </div>
                        <div className="text-[10px] text-text-secondary truncate">
                          {f.explanation}
                        </div>
                      </div>
                      <div className="font-mono text-xs">
                        <span className={f.triggered ? 'text-rose-light' : 'text-text-muted'}>
                          {f.triggered ? `+${f.contribution}` : `0/${f.weight}`}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="card p-4">
              <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-3">
                <Eye size={14} className="text-rose-light" /> Threat correlation
              </h2>
              {threat ? (
                <div className="space-y-2 text-xs">
                  <SectionCount label="Actors targeting"     value={threat.actors_targeting?.length || 0} />
                  <SectionCount label="Active CVEs"           value={threat.active_cves?.length || 0} />
                  <SectionCount label="KEV-flagged CVEs"      value={threat.kev_cves?.length || 0} highlight />
                  <SectionCount label="Ransomware proximity"  value={threat.ransomware_proximity?.length || 0} />
                  <SectionCount label="News clusters"          value={threat.news_clusters?.length || 0} />
                  <SectionCount label="Open conflicts"         value={threat.open_conflicts?.length || 0} highlight={threat.open_conflicts?.length > 0} />
                  <SectionCount label="Recent write-backs"     value={threat.recent_writebacks?.length || 0} />
                </div>
              ) : (
                <p className="text-xs text-text-muted">Loading threat correlation…</p>
              )}
              <p className="text-[10px] text-text-muted mt-3">
                Cross-domain correlations light up as Phases 5 (vuln scanners) and 9 (correlation engine) land.
              </p>
            </div>

            <div className="card p-4">
              <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-3">
                <Clock size={14} /> Timeline
              </h2>
              <div className="space-y-1.5 max-h-[400px] overflow-y-auto pr-2">
                {data.timeline.length === 0 && (
                  <p className="text-xs text-text-muted">No events yet.</p>
                )}
                {data.timeline.map((e, i) => (
                  <div key={i} className="flex items-start gap-2 text-[11px] py-1 border-b border-border/50 last:border-0">
                    <span className={clsx(
                      'badge text-[9px] mt-0.5 shrink-0 border',
                      e.kind === 'observation' ? 'bg-blue/10 text-blue-light border-blue/30' :
                      e.kind === 'conflict'    ? 'bg-amber/10 text-amber-light border-amber/30' :
                                                  'bg-purple/10 text-purple-light border-purple/30'
                    )}>
                      {e.kind}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-text-primary">
                        {e.field && <span className="font-mono text-[10px]">{e.field}</span>}
                        {e.source && <span className="text-text-muted"> · {e.source}</span>}
                        {e.event && <span className="text-text-muted"> · {e.event}</span>}
                      </div>
                      {e.value !== undefined && (
                        <div className="text-text-secondary text-[10px] font-mono truncate">
                          {JSON.stringify(e.value)}
                        </div>
                      )}
                      {e.occurred_at && (
                        <div className="text-[9px] text-text-muted font-mono">
                          {new Date(e.occurred_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


function FieldRow({ field, observations }: { field: string; observations: FieldObservation[] }) {
  // Group by canonical value to highlight disagreements
  const byValue = new Map<string, FieldObservation[]>()
  for (const o of observations) {
    const key = JSON.stringify(o.value)
    if (!byValue.has(key)) byValue.set(key, [])
    byValue.get(key)!.push(o)
  }
  const distinct = byValue.size

  return (
    <div className={clsx(
      'p-2.5 rounded border',
      distinct > 1 ? 'border-amber/40 bg-amber/5' : 'border-border bg-surface-card',
    )}>
      <div className="flex items-center gap-2 mb-1.5">
        <code className="text-xs font-mono text-text-primary font-semibold">{field}</code>
        {distinct > 1 && (
          <span className="badge text-[9px] bg-amber/10 text-amber-light border border-amber/30">
            {distinct} distinct values
          </span>
        )}
      </div>
      <div className="space-y-0.5">
        {Array.from(byValue.entries()).map(([key, obs], i) => (
          <div key={i} className="flex items-start gap-2 text-[11px]">
            <span className="font-mono text-[10px] text-text-secondary truncate flex-1 max-w-md">{key}</span>
            <div className="flex flex-wrap gap-1">
              {obs.map((o, j) => (
                <span key={j} className={clsx(
                  'badge text-[9px] border',
                  ROLE_TONE[o.role] || 'bg-surface-raised text-text-muted border-border'
                )}>
                  {o.source}{o.role !== 'observation' && ` (${o.role})`}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}


function SectionCount({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-text-muted">{label}</span>
      <span className={clsx(
        'font-mono font-bold',
        highlight && value > 0 ? 'text-rose-light' : value > 0 ? 'text-text-primary' : 'text-text-muted'
      )}>{value}</span>
    </div>
  )
}
